import { memo, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import LangLink from '../components/LangLink'
import Section from '../components/Section'
import { products, type Grade } from '../data/products'

type PolymerFilter = 'ALL' | 'PP' | 'PE'

// The MFI text field in the source spreadsheet sometimes carries Excel
// serial-date artefacts (e.g. "2026-10-14 00:00:00"). Strip those for
// display only; src/data/products.ts is not modified.
function cleanSpec(value: string): string {
  const out = value
    .replace(/\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}/g, '')
    .replace(/\s*\/\s*/g, ' / ')
    .replace(/^\s*\/\s*|\s*\/\s*$/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
  return out || '-'
}

// A React key needs to be stable across filtering/searching (so React can
// keep reusing the same DOM row instead of tearing it down and rebuilding
// it) and unique - grade.grade alone isn't: two grades repeat within PP
// Film (F800E, F800EDF). Computed once from the untouched source data (not
// the filtered view), so the key never depends on which items are
// currently visible.
const productsWithKeys = products.map((p) => ({
  ...p,
  categories: p.categories.map((c) => ({
    ...c,
    grades: c.grades.map((g, i) => ({ ...g, _key: `${c.name}-${i}` })),
  })),
}))

interface KeyedGrade extends Grade {
  _key: string
}

interface CategoryAccordionProps {
  name: string
  grades: KeyedGrade[]
  searchActive: boolean
}

// The table rows only mount once this category has actually been opened
// (by the visitor clicking it, or by a matching search auto-opening it) -
// not on first render. Before this, every one of the 197 grade rows across
// all 7 categories sat in the DOM from the start, just hidden by the closed
// <details>; Lighthouse's mainthread-work-breakdown on this page showed
// Style & Layout (266ms) as the single largest cost, well ahead of script
// execution (167ms), which matches a large hidden DOM more than it
// matches JS work. Once opened, content stays mounted (no un-mount on
// close) - simpler, and there's no benefit to tearing it down again.
// React.memo so a re-render of ProductsPage (e.g. from typing in a
// different, still-matching category's row) doesn't also re-render every
// other still-open category's 197-row-wide sibling tree for nothing.
const CategoryAccordion = memo(function CategoryAccordion({
  name,
  grades,
  searchActive,
}: CategoryAccordionProps) {
  const { t } = useTranslation()
  const [everOpened, setEverOpened] = useState(searchActive)
  const showRows = everOpened || searchActive

  return (
    <details
      className="products-cat"
      open={searchActive || undefined}
      onToggle={(e) => {
        if (e.currentTarget.open) setEverOpened(true)
      }}
    >
      <summary>
        <span className="products-cat-name">
          {t(`products.categories.${name}`, name)}
        </span>
        <span className="products-cat-count">
          {t('products.count', { count: grades.length })}
        </span>
      </summary>
      <div className="products-cat-panel">
        {showRows && (
          <div className="products-table-wrap">
            <table className="products-table">
              <thead>
                <tr>
                  <th>{t('products.th.grade')}</th>
                  <th>{t('products.th.application')}</th>
                  <th>{t('products.th.mfi')}</th>
                  <th>{t('products.th.feedstock')}</th>
                </tr>
              </thead>
              <tbody>
                {grades.map((g) => (
                  <tr key={g._key}>
                    <td data-label={t('products.th.grade')}>{g.grade}</td>
                    <td data-label={t('products.th.application')}>
                      {g.group}
                    </td>
                    <td data-label={t('products.th.mfi')}>
                      {cleanSpec(g.mfi)}
                    </td>
                    <td data-label={t('products.th.feedstock')}>
                      {g.base || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </details>
  )
})

function ProductsPage() {
  const { t } = useTranslation()
  const [polymer, setPolymer] = useState<PolymerFilter>('ALL')
  // The input's own value updates instantly on every keystroke so typing
  // never feels laggy; the actual filtering (and the accordion-open state
  // tied to it) runs off a debounced copy, so 197 grades aren't re-filtered
  // and re-rendered on every single keystroke - measured at ~20-58ms of
  // render/commit cost per keystroke on this page before this change.
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQuery(query), 200)
    return () => window.clearTimeout(id)
  }, [query])
  const searchActive = debouncedQuery.trim().length > 0

  const visible = useMemo(() => {
    const needle = debouncedQuery.trim().toLowerCase()
    return productsWithKeys
      .filter((p) => polymer === 'ALL' || p.code === polymer)
      .map((p) => ({
        ...p,
        categories: p.categories
          .map((c) => ({
            ...c,
            grades: needle
              ? c.grades.filter(
                  (g) =>
                    g.grade.toLowerCase().includes(needle) ||
                    g.group.toLowerCase().includes(needle),
                )
              : c.grades,
          }))
          .filter((c) => c.grades.length > 0),
      }))
      .filter((p) => p.categories.length > 0)
  }, [polymer, debouncedQuery])

  const total = visible.reduce(
    (sum, p) => sum + p.categories.reduce((s, c) => s + c.grades.length, 0),
    0,
  )

  return (
    <div className="products-page">
      {/* ---------- page header ---------- */}
      <Section background="surface" space="md" className="products-hero">
        <p className="eyebrow">{t('products.eyebrow')}</p>
        <h1 className="display">{t('products.title')}</h1>
        <p className="products-lead">{t('products.intro')}</p>
      </Section>

      {/* ---------- catalogue: sticky filter + accordions ---------- */}
      <Section background="beige" space="md" className="products-catalogue">
        <div className="products-filter">
          <div
            className="products-toggle"
            role="group"
            aria-label={t('products.filterAria')}
          >
            {(['ALL', 'PP', 'PE'] as const).map((code) => (
              <button
                key={code}
                type="button"
                className={polymer === code ? 'is-active' : undefined}
                aria-pressed={polymer === code}
                onClick={() => setPolymer(code)}
              >
                {code === 'ALL' ? t('products.filterAll') : code}
              </button>
            ))}
          </div>
          <input
            type="search"
            className="products-search"
            placeholder={t('products.searchPlaceholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={t('products.searchAria')}
          />
        </div>

        <div className="products-meta">
          <span className="products-count" aria-live="polite">
            {t('products.count', { count: total })}
          </span>
          <span className="products-price-note">{t('products.priceNote')}</span>
        </div>

        {total === 0 ? (
          <p className="products-nomatch">{t('products.noMatch')}</p>
        ) : (
          visible.map((p) => (
            <div key={p.code} className="products-polymer">
              <h2>{t(`products.polymers.${p.name}`, p.name)}</h2>
              {p.categories.map((c) => (
                <CategoryAccordion
                  // remount when the search toggles so the "open while
                  // searching" default applies cleanly; stable within a
                  // search state so manual toggles persist.
                  key={c.name + (searchActive ? '-q' : '')}
                  name={c.name}
                  grades={c.grades}
                  searchActive={searchActive}
                />
              ))}
            </div>
          ))
        )}
      </Section>

      {/* ---------- quote CTA band ---------- */}
      <Section background="bordeaux" space="md" className="products-cta">
        <h2>{t('home.quote.heading')}</h2>
        <p>{t('home.quote.body')}</p>
        <LangLink to="/how-to-order" className="btn btn--on-dark">
          {t('home.cta')}
        </LangLink>
      </Section>
    </div>
  )
}

export default ProductsPage
